import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Paper } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface ImageUploaderProps {
    onUpload: (file: File) => Promise<void>;
    compact?: boolean;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ onUpload, compact = false }) => {
    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            await onUpload(acceptedFiles[0]);
        }
    }, [onUpload]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.heic', '.heif', '.webp']
        },
        maxFiles: 1
    });

    return (
        <Paper
            {...getRootProps()}
            sx={{
                p: compact ? 1.5 : 3,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'divider',
                '&:hover': {
                    bgcolor: 'action.hover'
                }
            }}
        >
            <input {...getInputProps()} />
            <Box sx={{ 
                display: 'flex', 
                flexDirection: compact ? 'row' : 'column', 
                alignItems: 'center', 
                gap: compact ? 1 : 2,
                justifyContent: compact ? 'center' : 'stretch'
            }}>
                <CloudUploadIcon sx={{ 
                    fontSize: compact ? 24 : 48, 
                    color: 'primary.main' 
                }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: compact ? 0.5 : 1 }}>
                    <Typography variant={compact ? "body1" : "h6"}>
                        {isDragActive
                            ? 'Drop the file here'
                            : compact 
                                ? 'Upload new image'
                                : 'Drag and drop an image here, or click to select'}
                    </Typography>
                    {!compact && (
                        <Typography variant="body2" color="text.secondary">
                            Supported formats: JPEG, JPG, PNG, HEIC, HEIF, WebP
                        </Typography>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};
